import { Box, IconButton, useTheme, Tooltip, Dialog, DialogContent, DialogTitle } from "@mui/material";
import CollectionsIcon from "@mui/icons-material/Collections";
import MapIcon from "@mui/icons-material/Map";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useState } from "react";

interface PropertyGalleryHeaderProps {
    images: string[];
    latitude?: number;
    longitude?: number;
    address?: string;
    virtualTourUrl?: string;
    photoDefault?: string;
}

export default function PropertyGalleryHeader({ images, latitude, longitude, address, virtualTourUrl, photoDefault = "" }: PropertyGalleryHeaderProps) {
    const theme = useTheme();
    const [mapOpen, setMapOpen] = useState(false);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    if (!images || images.length === 0) {
        return null;
    }

    // Filtrar imágenes reales (que no sean solo photoDefault o vacías)
    const realImages = images.filter(img => img && img !== photoDefault && img !== "");
    const hasOnlyOneImage = realImages.length === 1;

    // Tomar solo las 3 primeras imágenes, usando photoDefault como fallback
    const firstImage = images[0] || photoDefault || "";
    const secondImage = images[1] || photoDefault || "";
    const thirdImage = images[2] || photoDefault || "";

    const handleViewGallery = () => {
        setGalleryOpen(true);
    };

    const handleCloseGallery = () => {
        setGalleryOpen(false);
    };

    const handleViewMap = () => {
        if (latitude && longitude) {
            setMapOpen(true);
        } else {
            console.log("No coordinates available");
        }
    };

    const handleCloseMap = () => {
        setMapOpen(false);
    };

    const handleVirtualTour = () => {
        if (virtualTourUrl) {
            // Abrir el tour virtual en una nueva pestaña
            window.open(virtualTourUrl, '_blank', 'noopener,noreferrer');
        } else {
            console.log("No virtual tour URL available");
        }
    };

    const handleOpenLightbox = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const handleCloseLightbox = () => {
        setLightboxOpen(false);
    };

    const handleLightboxPrevious = () => {
        setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleLightboxNext = () => {
        setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    // Construir URL de Google Maps (usando iframe embed sin API key)
    const getGoogleMapsUrl = () => {
        if (latitude && longitude) {
            // Usar Google Maps embed sin API key
            return `https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
        }
        if (address) {
            // Usar dirección si no hay coordenadas
            return `https://www.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`;
        }
        return "";
    };

    return (
        <Box 
            sx={{ 
                mb: 4, 
                width: "100%",
                maxWidth: 1400,
                margin: "0 auto",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: hasOnlyOneImage 
                        ? "1fr" 
                        : { xs: "1fr", sm: "2fr 1fr" },
                    gridTemplateRows: hasOnlyOneImage 
                        ? "1fr" 
                        : { xs: "auto", sm: "1fr 1fr" },
                    gap: { xs: "6px", sm: "8px" },
                    height: { xs: "auto", sm: 350, md: 350, lg: 400 },
                    position: "relative",
                    borderRadius: "12px",
                    overflow: "hidden",
                }}
            >
                {/* Imagen grande a la izquierda (o única imagen) */}
                <Box
                    sx={{
                        gridRow: hasOnlyOneImage ? "span 1" : { xs: "span 1", sm: "span 2" },
                        position: "relative",
                        overflow: "hidden",
                        border: "3px solid",
                        borderColor: theme.palette.primary.main,
                        borderRadius: hasOnlyOneImage 
                            ? "12px" 
                            : { xs: "12px", sm: "12px 0 0 12px" },
                        height: { xs: 200, sm: "100%", md: "100%", lg: "100%" },
                        cursor: "pointer",
                    }}
                >
                    <Box
                        component="img"
                        src={firstImage}
                        alt="Property main view"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                    
                    {/* Action Buttons Overlay - Solo mostrar en la primera imagen si hay solo una imagen */}
                    {hasOnlyOneImage && (
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: { xs: 12, sm: 16, md: 20 },
                                left: { xs: "50%", sm: "auto" },
                                right: { xs: "auto", sm: 16, md: 20 },
                                transform: { xs: "translateX(-50%)", sm: "none" },
                                display: "flex",
                                gap: { xs: 4, sm: 4, md: 4 },
                                zIndex: 10,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Tooltip title="View Gallery" arrow>
                                <IconButton
                                    onClick={handleViewGallery}
                                    sx={{
                                        width: { xs: 50, sm: 52, md: 54 },
                                        height: { xs: 50, sm: 52, md: 54 },
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.primary.main,
                                        color: "white",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                                        "&:hover": {
                                            backgroundColor: theme.palette.secondary.main,
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    <CollectionsIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="View Map" arrow>
                                <IconButton
                                    onClick={handleViewMap}
                                    sx={{
                                        width: { xs: 50, sm: 52, md: 54 },
                                        height: { xs: 50, sm: 52, md: 54 },
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.primary.main,
                                        color: "white",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                                        "&:hover": {
                                            backgroundColor: theme.palette.secondary.main,
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    <MapIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={virtualTourUrl ? "Virtual Tour" : "Virtual Tour not available"} arrow>
                                <span>
                                    <IconButton
                                        onClick={handleVirtualTour}
                                        disabled={!virtualTourUrl}
                                        sx={{
                                            width: { xs: 50, sm: 52, md: 54 },
                                            height: { xs: 50, sm: 52, md: 54 },
                                            borderRadius: "50%",
                                            backgroundColor: virtualTourUrl ? theme.palette.primary.main : "rgba(0, 0, 0, 0.12)",
                                            color: virtualTourUrl ? "white" : "rgba(0, 0, 0, 0.26)",
                                            boxShadow: virtualTourUrl ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "none",
                                            cursor: virtualTourUrl ? "pointer" : "not-allowed",
                                            "&:hover": virtualTourUrl ? {
                                                backgroundColor: theme.palette.secondary.main,
                                                transform: "translateY(-4px)",
                                                boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                            } : {},
                                            "&.Mui-disabled": {
                                                backgroundColor: "rgba(0, 0, 0, 0.12)",
                                                color: "rgba(0, 0, 0, 0.26)",
                                            },
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <ViewInArIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>

                {/* Primera imagen pequeña (arriba derecha) - Solo mostrar si hay más de una imagen */}
                {!hasOnlyOneImage && (
                    <Box
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            border: "3px solid",
                            borderColor: theme.palette.primary.main,
                            borderRadius: { xs: "12px", sm: "0 12px 0 0" },
                            height: { xs: 100, sm: "100%", md: "100%", lg: "100%" },
                            cursor: "pointer",
                        }}
                    >
                        <Box
                            component="img"
                            src={secondImage}
                            alt="Property view 2"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                    </Box>
                )}

                {/* Segunda imagen pequeña (abajo derecha) con botones - Solo mostrar si hay más de una imagen */}
                {!hasOnlyOneImage && (
                    <Box
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            border: "3px solid",
                            borderColor: theme.palette.primary.main,
                            borderRadius: { xs: "12px", sm: "0 0 12px 0" },
                            height: { xs: 100, sm: "100%", md: "100%", lg: "100%" },
                            cursor: "pointer",
                        }}
                    >
                        <Box
                            component="img"
                            src={thirdImage}
                            alt="Property view 3"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />

                        {/* Action Buttons Overlay */}
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: { xs: 12, sm: 16, md: 20 },
                                left: { xs: "50%", sm: "auto" },
                                right: { xs: "auto", sm: 16, md: 20 },
                                transform: { xs: "translateX(-50%)", sm: "none" },
                                display: "flex",
                                gap: { xs: 4, sm: 4, md: 4 },
                                zIndex: 10,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Tooltip title="View Gallery" arrow>
                                <IconButton
                                    onClick={handleViewGallery}
                                    sx={{
                                        width: { xs: 50, sm: 52, md: 54 },
                                        height: { xs: 50, sm: 52, md: 54 },
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.primary.main,
                                        color: "white",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                                        "&:hover": {
                                            backgroundColor: theme.palette.secondary.main,
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    <CollectionsIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="View Map" arrow>
                                <IconButton
                                    onClick={handleViewMap}
                                    sx={{
                                        width: { xs: 50, sm: 52, md: 54 },
                                        height: { xs: 50, sm: 52, md: 54 },
                                        borderRadius: "50%",
                                        backgroundColor: theme.palette.primary.main,
                                        color: "white",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                                        "&:hover": {
                                            backgroundColor: theme.palette.secondary.main,
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    <MapIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={virtualTourUrl ? "Virtual Tour" : "Virtual Tour not available"} arrow>
                                <span>
                                    <IconButton
                                        onClick={handleVirtualTour}
                                        disabled={!virtualTourUrl}
                                        sx={{
                                            width: { xs: 50, sm: 52, md: 54 },
                                            height: { xs: 50, sm: 52, md: 54 },
                                            borderRadius: "50%",
                                            backgroundColor: virtualTourUrl ? theme.palette.primary.main : "rgba(0, 0, 0, 0.12)",
                                            color: virtualTourUrl ? "white" : "rgba(0, 0, 0, 0.26)",
                                            boxShadow: virtualTourUrl ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "none",
                                            cursor: virtualTourUrl ? "pointer" : "not-allowed",
                                            "&:hover": virtualTourUrl ? {
                                                backgroundColor: theme.palette.secondary.main,
                                                transform: "translateY(-4px)",
                                                boxShadow: "0 6px 20px rgba(176, 139, 79, 0.4)",
                                            } : {},
                                            "&.Mui-disabled": {
                                                backgroundColor: "rgba(0, 0, 0, 0.12)",
                                                color: "rgba(0, 0, 0, 0.26)",
                                            },
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <ViewInArIcon sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Map Modal */}
            <Dialog
                open={mapOpen}
                onClose={handleCloseMap}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        m: { xs: 2, sm: 3 },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 1,
                    }}
                >
                    <Box sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
                        Property Location
                    </Box>
                    <IconButton
                        onClick={handleCloseMap}
                        sx={{
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: { xs: 400, sm: 500, md: 600 },
                            borderRadius: "0 0 8px 8px",
                            overflow: "hidden",
                        }}
                    >
                        {getGoogleMapsUrl() ? (
                            <iframe
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                src={getGoogleMapsUrl()}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: theme.palette.grey[100],
                                }}
                            >
                                <Box sx={{ textAlign: "center", p: 3 }}>
                                    <MapIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                                    <Box sx={{ color: theme.palette.text.secondary }}>
                                        No location data available
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Gallery Modal */}
            <Dialog
                open={galleryOpen}
                onClose={handleCloseGallery}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        m: { xs: 1, sm: 2 },
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pt: { xs: 3, sm: 4 },
                        pb: { xs: 3, sm: 4 },
                        px: { xs: 3, sm: 4 },
                        backgroundColor: "white",
                        position: "relative",
                    }}
                >
                    <Box 
                        sx={{ 
                            fontWeight: "bold", 
                            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
                            color: theme.palette.primary.main,
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "100%",
                            textAlign: "center",
                            pointerEvents: "none",
                        }}
                    >
                        Property Gallery
                    </Box>
                    <IconButton
                        onClick={handleCloseGallery}
                        sx={{
                            position: "absolute",
                            right: { xs: 8, sm: 16 },
                            top: { xs: 8, sm: 16 },
                            backgroundColor: theme.palette.primary.main,
                            color: "white",
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            zIndex: 1,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent 
                    sx={{ 
                        p: { xs: 3, sm: 4 },
                        backgroundColor: theme.palette.background.paper,
                        overflowY: "auto",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                            gap: { xs: 2, sm: 2.5, md: 3 },
                        }}
                    >
                        {images.map((image, index) => (
                            <Box
                                key={index}
                                onClick={() => handleOpenLightbox(index)}
                                sx={{
                                    position: "relative",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    aspectRatio: "4/3",
                                    cursor: "pointer",
                                    "&:hover": {
                                        transform: "scale(1.02)",
                                        transition: "transform 0.3s ease",
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={image}
                                    alt={`Property view ${index + 1}`}
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Lightbox Modal */}
            <Dialog
                open={lightboxOpen}
                onClose={handleCloseLightbox}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.95)",
                        m: 0,
                        maxWidth: "100vw",
                        maxHeight: "100vh",
                        borderRadius: 0,
                    },
                }}
                sx={{
                    "& .MuiDialog-container": {
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "100vh",
                        overflow: "hidden",
                    }}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={handleCloseLightbox}
                        sx={{
                            position: "absolute",
                            top: 16,
                            right: 16,
                            zIndex: 10,
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            color: "white",
                            "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.3)",
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Previous Button */}
                    {images.length > 1 && (
                        <IconButton
                            onClick={handleLightboxPrevious}
                            sx={{
                                position: "absolute",
                                left: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                color: "white",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                                },
                            }}
                        >
                            <ArrowBackIosIcon />
                        </IconButton>
                    )}

                    {/* Next Button */}
                    {images.length > 1 && (
                        <IconButton
                            onClick={handleLightboxNext}
                            sx={{
                                position: "absolute",
                                right: 16,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                color: "white",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                                },
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    )}

                    {/* Main Image */}
                    <Box
                        component="img"
                        src={images[lightboxIndex]}
                        alt={`Property view ${lightboxIndex + 1}`}
                        sx={{
                            maxWidth: "90vw",
                            maxHeight: "90vh",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />

                    {/* Image Counter */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 24,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            color: "white",
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            fontSize: "0.9rem",
                        }}
                    >
                        {lightboxIndex + 1} / {images.length}
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

